import React, { useState, useMemo, useEffect } from 'react'
import * as Headless from '@headlessui/react'
import { Button } from '../../../components/catalyst/button'
import { Select } from '../../../components/catalyst/select'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../../components/catalyst/dialog'
import { Table, TableHead, TableBody, TableHeader, TableRow, TableCell } from '../../../components/catalyst/table'
import clsx from 'clsx'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getApp } from 'firebase/app'
import { useToast } from '../../../util/ToastProvider'
import { CheckCircleIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import { getAuth, sendEmailVerification } from 'firebase/auth'

export default function Administrators() {
  const functions = getFunctions(getApp())
  const manageProfileHandler = httpsCallable(functions, 'manageProfile')

  const [admins, setAdmins] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<any | null>(null)
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', role: '', status: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [emailSending, setEmailSending] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const toast = useToast();

  // 🔄 Fetch admins from backend
  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const response: any = await manageProfileHandler({ action: 'listAdmins' })
      const fetchedAdmins = response.data.data || []
      // Add local incremental IDs if missing
      const enriched = fetchedAdmins.map((a: any, index: number) => ({
        localId: index + 1,
        ...a,
      }))
      console.log(enriched)
      setAdmins(enriched)
    } catch (error: any) {
      console.error('❌ Error fetching admins:', error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdmins()
  }, [])

  // 🔍 Local filter + search
  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      const matchesSearch =
        admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = filterRole ? admin.role === filterRole : true
      const matchesStatus = filterStatus ? admin.status === filterStatus : true
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [admins, searchTerm, filterRole, filterStatus])

  // 📄 Pagination
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const visibleAdmins = filteredAdmins.slice(startIndex, startIndex + itemsPerPage)

  // ➕ Add / ✏️ Edit dialog
  const openAddDialog = () => {
    setNewAdmin({ name: '', email: '', role: '', status: 'active' })
    setEditMode(false)
    setDialogOpen(true)
  }

  const openEditDialog = (admin: any) => {
    setNewAdmin({ ...admin })
    setSelectedAdmin(admin)
    setEditMode(true)
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.role || !newAdmin.status) return;
  
    setIsSaving(true);
  
    try {
      const action = editMode ? "editAdmin" : "createAdmin";
  
      const response: any = await manageProfileHandler({
        action,
        data: newAdmin,
      });
  
      console.log("Response:", response);
  
      toast.success(response.data.message || "Success");
  
      await fetchAdmins();
  
      setDialogOpen(false);
      setNewAdmin({ name: "", email: "", role: "", status: "" });
      setSelectedAdmin(null);
    } catch (error: any) {
      console.error("❌ Error saving admin:", error);
      toast.error(error.message || "Failed to save admin");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameChange = (val:any) => {
    const regex = /^[A-Za-z ]*$/;
    if (!regex.test(val)) return; // block invalid chars
  
    setNewAdmin({ ...newAdmin, name: val });
  };

  const emailRegex =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  const isEmailValid = emailRegex.test(newAdmin.email.trim());
  

  const resendEmail = async (email: string) => {
    setEmailSending(email);
  
    try {
      const response: any = await manageProfileHandler({
        action: "resendEmail",
        data: { email },
      });
  
      console.log("Backend response:", response);
  
      // 🔥 Backend returned success: false → Show warning instead of error
      if (response.data.success === false) {
        toast.error(response.data.message);
        return;
      }
  
      // 🔥 Success case
      toast.success(response.data.message || "Email sent successfully");
    } catch (error: any) {
      console.error("❌ Error resending email", error);
    
      // Extract deep Firebase error
      const messageString = error.message || "";
      const raw = error?.message || error?.details || "";
    
      const isRateLimit =
        raw.includes("RESET_PASSWORD_EXCEED_LIMIT") ||
        messageString.includes("RESET_PASSWORD_EXCEED_LIMIT");
    
      if (isRateLimit) {
        toast.error("Too many reset attempts. Please try again after 1 hour.");
      } else {
        toast.error(error.message || "Error resending email");
      }
    }
     finally {
      setEmailSending(null);
    }
  };
  
  

  const isFormValid =
    newAdmin.name.trim() && 
    isEmailValid && 
    newAdmin.role && 
    newAdmin.status

  return (
    <div className="mx-8 my-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">Administrators</h1>
        <Button color="brand" onClick={openAddDialog} disabled={emailSending !== null}>
          Add administrator
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setCurrentPage(1)
          }}
          className="w-1/3 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900"
        />
        <Select
          value={filterRole}
          onChange={(e) => {
            setFilterRole(e.target.value)
            setCurrentPage(1)
          }}
        >
          <option value="">Filter by role</option>
          <option value="analyst">Analyst</option>
          <option value="approver">Approver</option>
        </Select>
        <Select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value)
            setCurrentPage(1)
          }}
        >
          <option value="">Filter by status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </div>

      {/* Table */}
{loading ? (
  <div className="text-center py-10 text-zinc-500 dark:text-zinc-400">
    Loading admins...
  </div>
) : admins.length === 0 ? (
  <div className="text-center py-10 text-zinc-500 dark:text-zinc-400">
    No administrators found.
  </div>
) : filteredAdmins.length === 0 ? (
  <div className="text-center py-10 text-zinc-500 dark:text-zinc-400">
    No results match your filters.
  </div>
) : (
  <Table striped bleed>
    <TableHead>
      <TableRow>
        <TableHeader>Name</TableHeader>
        <TableHeader>Email</TableHeader>
        <TableHeader>Role</TableHeader>
        <TableHeader>Status</TableHeader>
      </TableRow>
    </TableHead>

    <TableBody>
      {visibleAdmins.map((admin, index) => (
        <TableRow key={index}>
          <TableCell>
            <button
              onClick={() => openEditDialog(admin)}
              className="text-indigo-600 hover:underline font-medium"
            >
              {admin.name}
            </button>
          </TableCell>
          <TableCell>
            <div className='flex gap-2'>
            {admin.email} 
            {admin.emailVerified && (
              <CheckCircleIcon className="h-6 w-6 text-[rgba(0,150,137,1)]" />
            )}
            {!admin.emailVerified && (
              <button
              onClick={() => resendEmail(admin.email)}
              disabled={emailSending === admin.email}
              className={clsx(
                "flex items-center gap-1",
                emailSending === admin.email
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              )}
            >
              <EnvelopeIcon className={`h-6 w-6 ${emailSending === admin.email ? "animate-email-pop text-indigo-600" : "text-gray-500"}`} />
              {emailSending === admin.email ? "Sending..." : "Verify"}
            </button>
            
            )}

            </div>
            
            </TableCell>
          <TableCell className="capitalize">{admin.role}</TableCell>
          <TableCell>
            <span
              className={clsx(
                'px-3 py-1 rounded-md text-xs font-medium capitalize',
                admin.status === 'active'
                  ? 'bg-[rgba(236,253,245,1)] text-[rgba(0,79,59,1)]'
                  : 'bg-dark-bg-danger-medium text-dark-text-danger-strong'
              )}
            >
              {admin.status}
            </span>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)}

      {/* Pagination */}
      {!loading && (
        <div className="flex justify-center gap-6 items-center pt-4 border-t border-zinc-200 dark:border-zinc-800">
          <Button plain onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            ← Previous
          </Button>

          <div className="flex gap-2 items-center">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-sm font-medium',
                  currentPage === page
                    ? 'bg-indigo-600 text-white'
                    : 'text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 dark:text-zinc-300'
                )}
              >
                {page}
              </button>
            ))}
          </div>

          <Button
            plain
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </Button>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onClose={() => {}}>
        <DialogTitle>{editMode ? 'Edit Administrator' : 'Add Administrator'}</DialogTitle>

        <DialogBody>
          <div className="space-y-6">
            {/* Full name */}
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter full name"
                value={newAdmin.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full mt-1 rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900"
              />
            </div>

            {/* Email (disabled in edit mode) */}
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newAdmin.email}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setNewAdmin({ ...newAdmin, email: e.target.value });
                  }
                }}
                maxLength={100}
                disabled={editMode}
                className={clsx(
                  'w-full mt-1 rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900',
                  editMode && 'opacity-70 cursor-not-allowed bg-zinc-100 dark:bg-zinc-800'
                )}
              />
              {editMode && (
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  Email cannot be changed after creation.
                </p>
              )}
            </div>

            {/* Role */}
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Role <span className="text-red-500">*</span>
              </label>
              <Headless.RadioGroup
                value={newAdmin.role}
                onChange={(val) => setNewAdmin({ ...newAdmin, role: val })}
                className="mt-2 flex gap-4"
              >
                {['analyst', 'approver'].map((role) => (
                  <Headless.Radio key={role} value={role} className="flex items-center gap-2 cursor-pointer">
                    {({ checked }) => (
                      <>
                        <span
                          className={clsx(
                            'size-4 rounded-full border border-zinc-400 flex items-center justify-center',
                            checked && 'border-blue-600'
                          )}
                        >
                          {checked && <span className="size-2.5 rounded-full bg-blue-600" />}
                        </span>
                        <span className="text-sm capitalize text-zinc-700 dark:text-zinc-200">{role}</span>
                      </>
                    )}
                  </Headless.Radio>
                ))}
              </Headless.RadioGroup>
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Status <span className="text-red-500">*</span>
              </label>
              <Headless.RadioGroup
                value={newAdmin.status}
                onChange={(val) => setNewAdmin({ ...newAdmin, status: val })}
                className="mt-2 flex gap-4"
              >
                {['active', 'inactive'].map((status) => (
                  <Headless.Radio key={status} value={status} className="flex items-center gap-2 cursor-pointer">
                    {({ checked }) => (
                      <>
                        <span
                          className={clsx(
                            'size-4 rounded-full border border-zinc-400 flex items-center justify-center',
                            checked && 'border-blue-600'
                          )}
                        >
                          {checked && <span className="size-2.5 rounded-full bg-blue-600" />}
                        </span>
                        <span className="text-sm capitalize text-zinc-700 dark:text-zinc-200">{status}</span>
                      </>
                    )}
                  </Headless.Radio>
                ))}
              </Headless.RadioGroup>
            </div>
          </div>
        </DialogBody>

        <DialogActions>
          <Button outline onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button color="brand" onClick={handleSave} disabled={!isFormValid || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
  @keyframes email-pop {
    0% { transform: scale(1); }
    50% { transform: scale(1.15); }
    100% { transform: scale(1); }
  }
  .animate-email-pop {
    animation: email-pop 0.4s ease-in-out infinite;
  }
`}</style>
    </div>
  )
}
